import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React, { useContext, useState, useEffect } from 'react';
import { Grid, Button, Header, Icon, Loader, GridColumn, Confirm } from 'semantic-ui-react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import _isEmpty from 'lodash/isEmpty';
import { getPlaceOrderButtonText, getStripePublicKey } from './Stripe.utils';
import AuthenticatedUserAccordion from './components/AuthenticatedUserAccordion';
import GuestUserAccordion from './components/GuestUserAccordion';
import CartSummary from '../../../../domains/checkout/CartSummary';
import CartSummaryTray from '../../../../domains/checkout/CartSummaryTray';
import ReviewOrder from '../../../../domains/checkout/ReviewOrder';
import SuccessfulCheckoutConfirmation from '../../../../domains/checkout/SuccessfulCheckoutConfirmation';
import { TIP_KEY, SPECIAL_INSTRUCTIONS_KEY } from '../../../../hooks/use-checkout/use-checkout.constants';
import useResponsive from '../../../../hooks/use-responsive';
import { hasAdjustments } from '../../../../modules/ordering/components/CartAdjustments/CartAdjustments.utils';
import CartAdjustmentsModal from '../../../../modules/ordering/components/CartAdjustmentsModal';
import { isOrderClosed } from '../../../../services/checkout';
import { CheckoutContext } from '../../Checkout.context';
import './StripeCheckout.css';
var defaultProps = {};

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
var stripePublicKey = getStripePublicKey();
var stripePromise = loadStripe(stripePublicKey, {
  stripeAccount: process.env.STRIPE_CONNECTED_ACCOUNT_ID
});
export default function StripeCheckout() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    reviewingOrder = _useState2[0],
    setReviewingOrder = _useState2[1];
  var _useResponsive = useResponsive(),
    isSmallScreen = _useResponsive.isSmallScreen,
    isDesktop = _useResponsive.isDesktop;
  var _useContext = useContext(CheckoutContext),
    checkout = _useContext.checkout,
    onReturnToCatalogPage = _useContext.onReturnToCatalogPage;
  var checkoutComplete = checkout.checkoutComplete,
    startCheckout = checkout.startCheckout,
    closeCheckout = checkout.closeCheckout,
    acceptOrderAdjustments = checkout.acceptOrderAdjustments,
    editingCheckoutData = checkout.editingCheckoutData,
    order = checkout.order,
    updateOrderAttributes = checkout.updateOrderAttributes,
    checkoutDisabled = checkout.checkoutDisabled,
    setCheckoutDisabled = checkout.setCheckoutDisabled,
    checkoutLoading = checkout.checkoutLoading,
    unauthorized = checkout.unauthorized,
    getOrderItemOptions = checkout.getOrderItemOptions,
    deleteOrderItem = checkout.deleteOrderItem,
    deleteOrderItemByUuid = checkout.deleteOrderItemByUuid,
    updateOrderItem = checkout.updateOrderItem,
    updateOrderItemQuantity = checkout.updateOrderItemQuantity,
    onCheckoutDataChange = checkout.onCheckoutDataChange,
    recaptchaLoaded = checkout.recaptchaLoaded;
  var _ref = order || {},
    tip = _ref.tip_total,
    items = _ref.line_items;
  var orderHasAdjustments = hasAdjustments(items);

  // TODO get this from context/storage
  var isGuestUser = true;
  var displayGuestAccordion = isGuestUser;
  var displayAuthenticatedAccordion = !isGuestUser;
  useEffect( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          if (!recaptchaLoaded) {
            _context.next = 3;
            break;
          }
          _context.next = 3;
          return startCheckout();
        case 3:
        case "end":
          return _context.stop();
      }
    }, _callee);
  })), [recaptchaLoaded]);
  function onTipChange(_x, _x2) {
    return _onTipChange.apply(this, arguments);
  }
  function _onTipChange() {
    _onTipChange = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(e, _ref3) {
      var value, isPercentage, isDebouncing, payload;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) switch (_context2.prev = _context2.next) {
          case 0:
            value = _ref3.value, isPercentage = _ref3.isPercentage, isDebouncing = _ref3.isDebouncing;
            if (isDebouncing) {
              _context2.next = 6;
              break;
            }
            payload = isPercentage ? {
              tipPercentage: value
            } : {
              tip: value
            };
            onCheckoutDataChange({
              name: TIP_KEY,
              value: payload
            });
            _context2.next = 6;
            return updateOrderAttributes(payload);
          case 6:
            setCheckoutDisabled(isDebouncing);
          case 7:
          case "end":
            return _context2.stop();
        }
      }, _callee2);
    }));
    return _onTipChange.apply(this, arguments);
  }
  function onSpecialInstructionsChange(_x3, _x4) {
    return _onSpecialInstructionsChange.apply(this, arguments);
  }
  function _onSpecialInstructionsChange() {
    _onSpecialInstructionsChange = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(e, _ref4) {
      var value, payload;
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) switch (_context3.prev = _context3.next) {
          case 0:
            value = _ref4.value;
            payload = {
              specialInstructions: value
            };
            onCheckoutDataChange({
              name: SPECIAL_INSTRUCTIONS_KEY,
              value: payload
            });
            _context3.next = 5;
            return updateOrderAttributes(payload);
          case 5:
          case "end":
            return _context3.stop();
        }
      }, _callee3);
    }));
    return _onSpecialInstructionsChange.apply(this, arguments);
  }
  function onPlaceOrder(_x5) {
    return _onPlaceOrder.apply(this, arguments);
  }
  function _onPlaceOrder() {
    _onPlaceOrder = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(optIn) {
      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) switch (_context4.prev = _context4.next) {
          case 0:
            _context4.next = 2;
            return closeCheckout(optIn);
          case 2:
          case "end":
            return _context4.stop();
        }
      }, _callee4);
    }));
    return _onPlaceOrder.apply(this, arguments);
  }
  function finishFillingInformation() {
    setReviewingOrder(true);
  }
  var InvalidOrderModal = /*#__PURE__*/React.createElement(Confirm, {
    open: unauthorized,
    className: "invalid-order-modal",
    dimmer: "inverted",
    header: "Your order is closed",
    content: "The order you requested is now closed or expired. Please create a new order at your convenience.",
    onConfirm: onReturnToCatalogPage,
    confirmButton: "Ok",
    cancelButton: null,
    size: "tiny",
    closeOnDimmerClick: false
  });
  if (_isEmpty(order) || !recaptchaLoaded) {
    return /*#__PURE__*/React.createElement(Loader, {
      active: true
    });
  }
  if (unauthorized) {
    return InvalidOrderModal;
  }
  if (checkoutComplete || isOrderClosed(order)) {
    return /*#__PURE__*/React.createElement("div", {
      className: "stripe-checkout"
    }, /*#__PURE__*/React.createElement("div", {
      className: "payment-complete-container"
    }, /*#__PURE__*/React.createElement(SuccessfulCheckoutConfirmation, {
      cart: order,
      tip: tip
    })));
  }
  return /*#__PURE__*/React.createElement("div", {
    className: "stripe-checkout"
  }, /*#__PURE__*/React.createElement(Elements, {
    stripe: stripePromise
  }, /*#__PURE__*/React.createElement(CartAdjustmentsModal, {
    open: orderHasAdjustments,
    items: items,
    onComplete: acceptOrderAdjustments,
    onRemoveItem: deleteOrderItemByUuid
  }), /*#__PURE__*/React.createElement(Grid, {
    columns: 2,
    stackable: true
  }, InvalidOrderModal, /*#__PURE__*/React.createElement(GridColumn, {
    width: 16,
    className: "header-menu-column"
  }, (!reviewingOrder || isDesktop) && /*#__PURE__*/React.createElement(Button, {
    className: "go-back-to-catalog-button",
    onClick: onReturnToCatalogPage
  }, "Go back")), /*#__PURE__*/React.createElement(Grid.Column, {
    width: 9,
    className: "accordion-column"
  }, isSmallScreen && reviewingOrder && /*#__PURE__*/React.createElement(Header, {
    as: "h3",
    className: "review-order-header"
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "angle left",
    onClick: onReturnToCatalogPage
  }), /*#__PURE__*/React.createElement(Header.Content, null, "Review Order"), /*#__PURE__*/React.createElement("div", null)), displayGuestAccordion && /*#__PURE__*/React.createElement(GuestUserAccordion, {
    onReviewOrder: finishFillingInformation,
    reviewingOrder: reviewingOrder
  }), displayAuthenticatedAccordion && /*#__PURE__*/React.createElement(AuthenticatedUserAccordion, {
    onReviewOrder: finishFillingInformation,
    reviewingOrder: reviewingOrder
  })), /*#__PURE__*/React.createElement(Grid.Column, {
    width: 7,
    className: reviewingOrder ? 'review-cart-column' : 'desktop-cart-summary-column'
  }, reviewingOrder ? /*#__PURE__*/React.createElement(ReviewOrder, {
    cart: order,
    onPlaceOrder: onPlaceOrder,
    disabled: editingCheckoutData || checkoutDisabled,
    placeOrderButtonContent: getPlaceOrderButtonText(order, isSmallScreen),
    asTray: isSmallScreen,
    tip: tip,
    onTipChange: onTipChange,
    onDeleteCartItem: deleteOrderItem,
    onUpdateCartItem: updateOrderItem,
    onUpdateCartItemQuantity: updateOrderItemQuantity,
    getOrderItemOptions: getOrderItemOptions,
    loading: checkoutLoading,
    onSpecialInstructionsChange: onSpecialInstructionsChange
  }) : /*#__PURE__*/React.createElement(CartSummary, {
    cart: order,
    onDeleteCartItem: deleteOrderItem,
    onUpdateCartItem: updateOrderItem,
    tip: tip,
    onUpdateCartItemQuantity: updateOrderItemQuantity,
    getOrderItemOptions: getOrderItemOptions,
    loading: checkoutLoading
  }))), !reviewingOrder && /*#__PURE__*/React.createElement("div", {
    className: "mobile-cart-summary"
  }, /*#__PURE__*/React.createElement(CartSummaryTray, {
    cart: order,
    onDeleteCartItem: deleteOrderItem,
    onUpdateCartItem: updateOrderItem,
    onUpdateCartItemQuantity: updateOrderItemQuantity,
    getOrderItemOptions: getOrderItemOptions,
    loading: checkoutLoading
  }))));
}
StripeCheckout.defaultProps = defaultProps;